import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, Loader2, Package, Plus, Receipt, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LeistungsdatumPicker, {
  emptyLeistungsdatum, formatLeistungsdatum, parseLeistungsdatum, validateLeistungsdatum,
} from "@/components/crm/LeistungsdatumPicker";

const RATE_STORAGE_KEY = "blessing_hourly_rate";

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmt(v) {
  return (Math.round((Number(v) || 0) * 100) / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function fmtHours(v) {
  return (Math.round((Number(v) || 0) * 100) / 100).toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

function loadSavedRate() {
  try { return localStorage.getItem(RATE_STORAGE_KEY) || ""; } catch { return ""; }
}

function saveRate(v) {
  try { localStorage.setItem(RATE_STORAGE_KEY, String(v)); } catch { /* ignore */ }
}

// rate: "" → Standard-Stundensatz der Rechnung verwenden
const newHourlyRow = () => ({ type: "hourly", description: "", hours: "", rate: "" });
const newFlatRow = () => ({ type: "flat", description: "", quantity: 1, unit: "Pauschal", unit_price: "" });
// Monatsposition = Mengenposition mit Einheit „Monat“ (z. B. Rundum-Sorglos-Paket)
const newMonthlyRow = () => ({ type: "flat", description: "", quantity: 1, unit: "Monat", unit_price: "" });

const rowFromItem = (it) => (it.hourly
  ? { type: "hourly", description: it.description || "", hours: String(it.quantity ?? ""), rate: String(it.unit_price ?? "") }
  : {
    type: "flat",
    description: it.description || "",
    quantity: it.quantity ?? 1,
    unit: it.unit || "Pauschal",
    unit_price: String(it.unit_price ?? ""),
  });

// invoice gesetzt → Bearbeiten-Modus (Rechnungsnummer bleibt unverändert)
export default function ManualInvoiceDialog({ open, onOpenChange, onCreated, invoice = null }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [defaultRate, setDefaultRate] = useState("");
  const [rows, setRows] = useState([newHourlyRow()]);
  const [leistungsdatum, setLeistungsdatum] = useState(emptyLeistungsdatum());
  const [ausstellung, setAusstellung] = useState(todayIso());
  const [dueDays, setDueDays] = useState(14);
  const [startNumber, setStartNumber] = useState("");
  const [kleinunternehmer, setKleinunternehmer] = useState(true);
  const [taxRate, setTaxRate] = useState(19);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/customers")
      .then((r) => setCustomers(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    setStartNumber("");
    if (invoice) {
      setCustomerId(invoice.customer_id);
      setDefaultRate(invoice.hourly_rate != null ? String(invoice.hourly_rate) : loadSavedRate());
      setRows((invoice.items || []).length ? invoice.items.map(rowFromItem) : [newHourlyRow()]);
      setLeistungsdatum(parseLeistungsdatum(invoice.rechnungszeitraum));
      setAusstellung(invoice.ausstellungsdatum || (invoice.created_at || "").slice(0, 10) || todayIso());
      setDueDays(invoice.payment_due_days ?? 14);
      setKleinunternehmer(!!invoice.kleinunternehmer);
      // Bei § 19 UStG ist tax_rate 0 gespeichert – beim Abwählen wieder 19 % vorschlagen.
      setTaxRate(invoice.kleinunternehmer ? 19 : invoice.tax_rate);
      return;
    }
    setCustomerId("");
    setDefaultRate(loadSavedRate());
    setRows([newHourlyRow()]);
    setLeistungsdatum(emptyLeistungsdatum());
    setAusstellung(todayIso());
    setDueDays(14);
    setKleinunternehmer(true);
    setTaxRate(19);
  }, [open, invoice]);

  const rateOf = (row) => (row.rate !== "" ? Number(row.rate) : Number(defaultRate)) || 0;
  const lineTotal = (row) => (row.type === "hourly"
    ? (Number(row.hours) || 0) * rateOf(row)
    : (Number(row.quantity) || 0) * (Number(row.unit_price) || 0));

  const totals = useMemo(() => {
    const effectiveTax = kleinunternehmer ? 0 : (Number(taxRate) || 0);
    const sub = rows.reduce((s, r) => s + lineTotal(r), 0);
    const hours = rows.filter((r) => r.type === "hourly").reduce((s, r) => s + (Number(r.hours) || 0), 0);
    const tax = sub * effectiveTax / 100;
    return { sub, tax, total: sub + tax, hours };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, defaultRate, taxRate, kleinunternehmer]);

  const updateRow = (idx, patch) => setRows((arr) => arr.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx) => setRows((arr) => arr.filter((_, i) => i !== idx));

  async function save() {
    if (!customerId) { toast.error("Bitte Kunde wählen"); return; }
    const filled = rows.filter((r) => r.description.trim().length > 0);
    if (filled.length === 0) { toast.error("Bitte mindestens eine Leistungsposition angeben"); return; }
    for (const r of filled) {
      const name = r.description.trim();
      if (r.type === "hourly") {
        if (!(Number(r.hours) > 0)) { toast.error(`„${name}“: Bitte Anzahl Stunden eintragen`); return; }
        if (!(rateOf(r) > 0)) { toast.error(`„${name}“: Bitte Stundensatz angeben`); return; }
      } else if (!(Number(r.quantity) > 0)) {
        toast.error(`„${name}“: Menge muss größer als 0 sein`); return;
      }
    }
    const leistungsdatumError = validateLeistungsdatum(leistungsdatum);
    if (leistungsdatumError) { toast.error(leistungsdatumError); return; }
    if (!ausstellung) { toast.error("Bitte Ausstellungsdatum wählen"); return; }
    const parsedStart = parseInt(startNumber, 10);
    if (startNumber && (!Number.isFinite(parsedStart) || parsedStart <= 0)) {
      toast.error("Startnummer muss eine positive Zahl sein"); return;
    }

    const hasHourly = filled.some((r) => r.type === "hourly");
    const payload = {
      customer_id: customerId,
      items: filled.map((r) => (r.type === "hourly"
        ? { description: r.description.trim(), quantity: Number(r.hours), unit: "Std.", unit_price: rateOf(r), hourly: true }
        : {
          description: r.description.trim(),
          quantity: Number(r.quantity),
          unit: r.unit || "Pauschal",
          unit_price: Number(r.unit_price) || 0,
          hourly: false,
        })),
      tax_rate: Number(taxRate) || 0,
      kleinunternehmer,
      payment_due_days: Number(dueDays) || 14,
      ausstellungsdatum: ausstellung,
      rechnungszeitraum: formatLeistungsdatum(leistungsdatum),
      hourly_rate: hasHourly && Number(defaultRate) > 0 ? Number(defaultRate) : null,
    };
    if (parsedStart > 0 && !invoice) payload.start_number = parsedStart;

    setSaving(true);
    try {
      if (invoice) {
        await api.put(`/invoices/${invoice.id}`, payload);
        toast.success(`Rechnung ${invoice.invoice_number} aktualisiert`);
      } else {
        const r = await api.post("/invoices", payload);
        if (Number(defaultRate) > 0) saveRate(defaultRate);
        toast.success(`Rechnung ${r.data.invoice_number} erstellt`);
      }
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {invoice ? `Rechnung ${invoice.invoice_number} bearbeiten` : "Manuelle Rechnung"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto p-1">
          {invoice && invoice.status !== "entwurf" && (
            <div className="text-xs rounded-lg border border-[#F5D9CC] bg-[#FDF3EE] text-[#7A2E14] px-3 py-2" data-testid="invoice-edit-sent-hint">
              Diese Rechnung wurde bereits versendet. Die Rechnungsnummer bleibt gleich –
              senden Sie die geänderte Fassung bei Bedarf erneut an den Kunden.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label>Kunde *</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="mt-1.5" data-testid="manual-invoice-customer-select">
                  <SelectValue placeholder="Kunde wählen" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.address_city ? ` — ${c.address_city}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="manual-invoice-rate">Stundensatz (€/Std.)</Label>
              <Input
                id="manual-invoice-rate"
                type="number" min="0" step="0.5"
                className="mt-1.5 text-right"
                placeholder="z. B. 45"
                value={defaultRate}
                onChange={(e) => setDefaultRate(e.target.value)}
                data-testid="manual-invoice-rate-input"
              />
            </div>
          </div>

          <div className="border border-[#E5E7EB] rounded-lg">
            <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-[#F5F6F0] text-[11px] font-medium uppercase tracking-wider text-[#5C6B5D]">
              <div className="col-span-5">Leistung</div>
              <div className="col-span-2">Stunden / Menge</div>
              <div className="col-span-2 text-right">Satz / Preis</div>
              <div className="col-span-2 text-right">Betrag</div>
              <div className="col-span-1" />
            </div>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-[#E5E7EB] items-start" data-testid={`manual-invoice-row-${i}`}>
                <div className="col-span-12 md:col-span-5 space-y-1">
                  <div className="flex items-center gap-1.5 text-[11px] text-[#5C6B5D]">
                    {r.type === "hourly"
                      ? <><Clock className="w-3 h-3" /> Stundenabrechnung</>
                      : r.unit === "Monat"
                        ? <><CalendarDays className="w-3 h-3" /> Monatlich</>
                        : <><Package className="w-3 h-3" /> Pauschal / Menge</>}
                  </div>
                  <Input
                    className="h-8"
                    value={r.description}
                    onChange={(e) => updateRow(i, { description: e.target.value })}
                    placeholder={r.type === "hourly"
                      ? "z. B. Gartenarbeiten 05.10."
                      : r.unit === "Monat" ? "z. B. Rundum-Sorglos-Paket Oktober" : "z. B. Grünschnittentsorgung"}
                    data-testid={`manual-invoice-desc-${i}`}
                  />
                </div>
                {r.type === "hourly" ? (
                  <>
                    <div className="col-span-4 md:col-span-2 md:pt-5">
                      <Input
                        className="h-8" type="number" min="0" step="0.25" placeholder="Std."
                        value={r.hours} onChange={(e) => updateRow(i, { hours: e.target.value })}
                        data-testid={`manual-invoice-hours-${i}`}
                        aria-label="Stunden"
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2 md:pt-5">
                      <Input
                        className="h-8 text-right" type="number" min="0" step="0.5"
                        placeholder={defaultRate ? `${defaultRate} €/Std.` : "€/Std."}
                        value={r.rate} onChange={(e) => updateRow(i, { rate: e.target.value })}
                        data-testid={`manual-invoice-row-rate-${i}`}
                        aria-label="Stundensatz für diese Position"
                        title="Leer lassen, um den Standard-Stundensatz zu verwenden"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-4 md:col-span-2 md:pt-5 flex gap-1">
                      <Input
                        className="h-8 w-1/2" type="number" min="0" step="0.5" placeholder="Menge"
                        value={r.quantity} onChange={(e) => updateRow(i, { quantity: e.target.value })}
                        data-testid={`manual-invoice-qty-${i}`}
                        aria-label="Menge"
                      />
                      <Input
                        className="h-8 w-1/2 px-2" placeholder="Einheit"
                        value={r.unit} onChange={(e) => updateRow(i, { unit: e.target.value })}
                        data-testid={`manual-invoice-unit-${i}`}
                        aria-label="Einheit"
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2 md:pt-5">
                      <Input
                        className="h-8 text-right" type="number" min="0" step="0.01" placeholder="Preis"
                        value={r.unit_price} onChange={(e) => updateRow(i, { unit_price: e.target.value })}
                        data-testid={`manual-invoice-price-${i}`}
                        aria-label="Einzelpreis"
                      />
                    </div>
                  </>
                )}
                <div className="col-span-3 md:col-span-2 md:pt-5 h-8 flex items-center justify-end font-mono text-sm">
                  {fmt(lineTotal(r))}
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="col-span-1 md:pt-5 h-8 flex items-center justify-center text-[#B85C38] hover:text-[#7A2E14]"
                  aria-label="Position entfernen"
                  data-testid={`manual-invoice-remove-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="px-3 py-2 border-t border-[#E5E7EB] flex flex-wrap gap-1">
              <Button variant="ghost" size="sm" onClick={() => setRows((a) => [...a, newHourlyRow()])} data-testid="manual-invoice-add-hourly">
                <Plus className="w-3.5 h-3.5 mr-1" /> Stundenposition
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setRows((a) => [...a, newFlatRow()])} data-testid="manual-invoice-add-flat">
                <Plus className="w-3.5 h-3.5 mr-1" /> Pauschalposition
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setRows((a) => [...a, newMonthlyRow()])} data-testid="manual-invoice-add-monthly">
                <Plus className="w-3.5 h-3.5 mr-1" /> Monatsposition
              </Button>
            </div>
          </div>
          <div className="text-[11px] text-[#5C6B5D] -mt-2">
            Stunden als Dezimalzahl eintragen (1,5 = 1 Std. 30 Min.). Ein Satz in der Zeile überschreibt den Standard-Stundensatz nur für diese Position.
          </div>

          <div>
            <Label>Leistungsdatum *</Label>
            <div className="mt-1.5">
              <LeistungsdatumPicker
                value={leistungsdatum}
                onChange={setLeistungsdatum}
                testIdPrefix="manual-invoice-leistungsdatum"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="manual-invoice-ausstellung">Ausstellungsdatum</Label>
              <Input
                id="manual-invoice-ausstellung" type="date" className="mt-1.5"
                value={ausstellung} onChange={(e) => setAusstellung(e.target.value)}
                data-testid="manual-invoice-ausstellung-input"
              />
            </div>
            <div>
              <Label htmlFor="manual-invoice-due">Zahlungsziel (Tage)</Label>
              <Input
                id="manual-invoice-due" type="number" min="0" className="mt-1.5"
                value={dueDays} onChange={(e) => setDueDays(e.target.value)}
                data-testid="manual-invoice-due-input"
              />
            </div>
          </div>

          {!invoice && (
            <div>
              <Label htmlFor="manual-invoice-start-number">Rechnungsnummer neu setzen (optional)</Label>
              <Input
                id="manual-invoice-start-number" type="number" min="1" step="1" className="mt-1.5"
                placeholder="Leer lassen, um fortlaufend weiterzuzählen"
                value={startNumber} onChange={(e) => setStartNumber(e.target.value)}
                data-testid="manual-invoice-start-number-input"
              />
            </div>
          )}

          <label
            htmlFor="manual-invoice-kleinunternehmer"
            className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6F0] px-3 py-2.5 cursor-pointer hover:border-[#6B7F3E]/60 transition"
          >
            <Checkbox
              id="manual-invoice-kleinunternehmer"
              checked={kleinunternehmer}
              onCheckedChange={(v) => setKleinunternehmer(v === true)}
              data-testid="manual-invoice-kleinunternehmer-checkbox"
              className="mt-0.5"
            />
            <div className="text-sm text-[#2C3E2D] leading-snug">
              <div className="font-medium">Keine Mehrwertsteuer (§ 19 UStG)</div>
              <div className="text-xs text-[#5C6B5D] mt-0.5">
                Aktiviert: MwSt. wird auf 0 % gesetzt und die Klausel „Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." erscheint auf der Rechnung.
              </div>
            </div>
          </label>

          <div className="flex items-start justify-between gap-4 flex-wrap border-t border-[#E5E7EB] pt-4">
            <div className="text-sm space-y-1">
              <div>
                MwSt.
                <Input
                  type="number" className="inline-block w-20 h-8 ml-2 text-center"
                  value={kleinunternehmer ? 0 : taxRate} onChange={(e) => setTaxRate(e.target.value)}
                  disabled={kleinunternehmer}
                  data-testid="manual-invoice-tax-input"
                /> %
              </div>
              {totals.hours > 0 && (
                <div className="text-[#5C6B5D]" data-testid="manual-invoice-hours-total">
                  Stunden gesamt: <span className="font-mono">{fmtHours(totals.hours)} Std.</span>
                </div>
              )}
            </div>
            <div className="text-right text-sm space-y-0.5 ml-auto">
              <div>Zwischensumme: <span className="font-mono">{fmt(totals.sub)}</span></div>
              <div>MwSt: <span className="font-mono">{fmt(totals.tax)}</span></div>
              <div className="text-lg font-semibold text-[#2C3E2D]" data-testid="manual-invoice-total">
                Gesamt: <span className="font-mono">{fmt(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
          <Button
            className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
            onClick={save} disabled={saving}
            data-testid="manual-invoice-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Receipt className="w-4 h-4 mr-1" /> {invoice ? "Änderungen speichern" : "Rechnung erstellen"}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
